<template>
  <RevLink
    v-if="parsedForm === 'link'"
    :id="ctaId"
    :ariaLabelledby="parsedAriaLabelledBy"
    data-test="spotlight-link"
    :rel="link.rel"
    :target="link.target"
    :to="link.href"
    @click="onCTAClick"
  >
    {{ label }}
  </RevLink>

  <RevButton
    v-if="parsedForm === 'square'"
    :id="ctaId"
    :ariaLabelledby="parsedAriaLabelledBy"
    class="!max-w-fit !md:max-w-[326px] !w-full"
    data-test="spotlight-square-button"
    :rel="link.rel"
    size="medium"
    :target="link.target"
    :to="link.href"
    :variant="parsedVariant"
    @click="onCTAClick"
  >
    {{ label }}
  </RevButton>

  <component
    :is="multiple ? 'li' : 'div'"
    v-if="parsedForm === 'rounded'"
    :class="getCTAColors(parsedVariant).wrapper"
  >
    <RevButton
      :id="ctaId"
      :ariaLabelledby="parsedAriaLabelledBy"
      class="rounded-round h-fit"
      :class="getCTAColors(parsedVariant).button"
      data-test="spotlight-rounded-button"
      :rel="link.rel"
      size="small"
      :target="link.target"
      :to="link.href"
      variant="primary"
      @click="onCTAClick"
    >
      <span class="flex items-center">
        <span :class="getCTAColors(parsedVariant).text"> {{ label }} </span>

        <DynamicIcon
          v-if="icon"
          class="ml-4 mood-main !size-20"
          :class="getCTAColors(parsedVariant).text"
          v-bind="icon"
        />
      </span>
    </RevButton>
  </component>
</template>

<script setup lang="ts">
import type { ProductSpotlightCtaApiProps } from '@backmarket/http-api/src/api-specs-content/models/product-spotlight'
import type { Tracking } from '@backmarket/http-api/src/api-specs-content/models/tracking'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButton } from '@ds/components/Button'
import { RevLink } from '@ds/components/Link'

import DynamicIcon from '../../shared-components/DynamicIcon/DynamicIcon.vue'
import { getCTAColors } from '../../utils/CTAColorCombinations'

const {
  label,
  variant,
  form,
  link,
  icon,
  tracking,
  // For accessibility, use this prop when displaying a list of SpotlightCTAs
  multiple = false,
  // For accessibility, use this prop to link the CTA to another text field for more context on the CTA action
  ariaLabelledBy = '',
} = defineProps<
  ProductSpotlightCtaApiProps & {
    tracking: Tracking
    multiple?: boolean
    ariaLabelledBy?: string
  }
>()

const { trackClick } = useTracking()

const ctaId = `product-spotlight-cta-${form}-${variant}`
const parsedAriaLabelledBy = ariaLabelledBy
  ? `${ctaId} ${ariaLabelledBy}`
  : ctaId
const parsedVariant = variant ?? 'primary'
const parsedForm = form ?? 'link'

function onCTAClick() {
  trackClick({
    ...tracking,
    source: 'ProductSpotlight',
    name: label,
    link: link.href,
  })
}
</script>
